import React from 'react';
import { 
  Card, 
  Descriptions, 
  Tag, 
  Space, 
  Statistic, 
  Row, 
  Col, 
  List, 
  Avatar, 
  Empty,
  Divider,
  Badge,
  Typography
} from 'antd';
import { 
  ScheduleOutlined,
  CalendarOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { PlanogramTemplate, CycleConfig } from '../../../../types';

const { Text } = Typography;

interface CycleConfigurationProps {
  template: PlanogramTemplate;
}

const CycleConfiguration: React.FC<CycleConfigurationProps> = ({
  template
}) => {
  const { t } = useTranslation();
  const cycle = template.executionCycle;
  
  // Weekdays translation helper
  const getWeekdayTranslation = (day: string | number) => {
    const weekdays = t('planTemplate.detail.cycleConfig.weekdays', { returnObjects: true }) as string[];
    let dayIndex: number;
    
    if (typeof day === 'number') {
      dayIndex = day;
    } else {
      dayIndex = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'].indexOf(day);
    }
    
    return dayIndex >= 0 && dayIndex < weekdays.length ? weekdays[dayIndex] : String(day);
  };
  
  // Status translation helper
  const getStatusTranslation = (status: string) => {
    const statusKey = status.toLowerCase();
    return t(`planTemplate.detail.cycleConfig.status.${statusKey}`, status);
  };
  
  const getFrequencyDescription = () => {
    let desc = t('planTemplate.detail.cycleConfig.everyDays', { interval: cycle.interval });
    switch (cycle.frequencyType) {
      case 'Daily': return t('planTemplate.detail.cycleConfig.everyDays', { interval: cycle.interval });
      case 'Weekly': return t('planTemplate.detail.cycleConfig.everyWeeks', { interval: cycle.interval });
      case 'Monthly': return t('planTemplate.detail.cycleConfig.everyMonths', { interval: cycle.interval });
      case 'Yearly': return t('planTemplate.detail.cycleConfig.everyYears', { interval: cycle.interval });
      default: return desc;
    }
  };

  const getTerminationDescription = () => {
    switch (cycle.terminationType) {
      case 'Infinite Repeat': return t('planTemplate.detail.cycleConfig.infiniteRepeat');
      case 'End on Date': return t('planTemplate.detail.cycleConfig.endOnDate', { 
        date: cycle.endDate ? dayjs(cycle.endDate).format('YYYY-MM-DD') : t('planTemplate.detail.cycleConfig.notSet')
      });
      case 'Limited Count': return t('planTemplate.detail.cycleConfig.limitedCount', { count: cycle.repeatCount || 0 });
      default: return t('planTemplate.detail.cycleConfig.notSet');
    }
  };


  

  const calPreviewByDay = (cycle) => {
    if (!cycle.firstExecutionDate || !cycle.frequencyType) {
      return [];
    }
    const dates: string[] = [];
    const firstExecutionDate = dayjs(cycle.firstExecutionDate);
    let currentDate = firstExecutionDate.clone();
    // 如果 currentDate 在今天之前，则赋值为今天
    const today = dayjs().startOf('day');
    if (currentDate.isBefore(today)) {
      currentDate = today.clone();
    }
    const maxPreviewCount = 10; // Show up to 10 upcoming dates
    let count = 0;

    // Determine end condition
    const hasEndDate = cycle.terminationType === 'End on Date' && cycle.endDate;
    const hasRepeatLimit = cycle.terminationType === 'Limited Count' && cycle.repeatCount;
    const endDate = hasEndDate ? dayjs(cycle.endDate) : null;
    const maxCount = hasRepeatLimit ? Math.min(cycle.repeatCount!, maxPreviewCount) : maxPreviewCount;

    // currentDate = currentDate.add(cycleConfig.interval, 'day');
    
    while (count < maxCount) {
      // Check if we've reached the end date
      if (endDate && currentDate.isAfter(endDate)) {
        break;
      }

      // Add current date to preview
      dates.push(currentDate.format('YYYY-MM-DD'));
      count++;

      // Calculate next date based on frequency type
      currentDate = currentDate.add(cycle.interval, 'day');
    }

    return dates;
  }

  const calPreviewByWeek = (cycle) => {
    if (!cycle.firstExecutionDate || !cycle.frequencyType) {
      return [];
    }

    const dates: string[] = [];
    const firstExecutionDate = dayjs(cycle.firstExecutionDate);
    let currentDate = firstExecutionDate.clone();
    const maxPreviewCount = 10; // Show up to 10 upcoming dates
    let count = 0;

    // Determine end condition
    const hasEndDate = cycle.terminationType === 'End on Date' && cycle.endDate;
    const hasRepeatLimit = cycle.terminationType === 'Limited Count' && cycle.repeatCount;
    const endDate = hasEndDate ? dayjs(cycle.endDate) : null;
    const maxCount = hasRepeatLimit ? Math.min(cycle.repeatCount!, maxPreviewCount) : maxPreviewCount;

    currentDate = currentDate.day(1);
    const dayofWeeks = [];

    for (let i = 0; i < 7; i++) {
      if (cycle.weekDaySelection && cycle.weekDaySelection.length > 0) {
      // 先将日期加一天，确保我们查找的是首次执行日期之后的日期
      // currentDate = currentDate.add(1, 'day');
      
      // 寻找第一个匹配的星期几
        if (cycle.weekDaySelection.includes(currentDate.day())) {
          dayofWeeks.push(currentDate);
        }
        currentDate = currentDate.add(1, 'day');
      }
    }

    let today = dayjs().startOf('day');
    // 如果 firstExecutionDate 在今天之前，则使用今天作为开始日期进行查询
    if (firstExecutionDate.isBefore(today)) {
      // 重新设置 currentDate 和 dayofWeeks，从今天开始查找符合条件的日期
      currentDate = today.clone();
      dayofWeeks.length = 0;
      for (let i = 0; i < 7; i++) {
        if (cycle.weekDaySelection && cycle.weekDaySelection.length > 0) {
          if (cycle.weekDaySelection.includes(currentDate.day())) {
            dayofWeeks.push(currentDate);
          }
          currentDate = currentDate.add(1, 'day');
        }
      }
    }
    let currentCount = 0;
    for(let i = 0; i < 7; i++) {
      for(let j = 0; j < dayofWeeks.length; j++) {
        let date = dayofWeeks[j].add(i * cycle.interval, 'week');
        if (endDate && date.isAfter(endDate)) {
          break;
        }
        if (date.date() == today.date() || date.isAfter(today)) {
          dates.push(date.format('YYYY-MM-DD'));
          currentCount++;
        }
        if (currentCount >= maxCount) break;
      }
      if (currentCount >= maxCount) break;
    }

    return dates;
  }

  const calPreviewByMonth = (cycle) => {
    if (!cycle.firstExecutionDate || !cycle.frequencyType) {
      return [];
    }

    const dates: string[] = [];
    const firstExecutionDate = dayjs(cycle.firstExecutionDate);
    let currentDate = firstExecutionDate.clone();
    // 如果 currentDate 在今天之前，则赋值为今天
    const today = dayjs().startOf('day');
    if (currentDate.isBefore(today)) {
      currentDate = today.clone();
    }
    const maxPreviewCount = 10; // Show up to 10 upcoming dates
    let count = 0;

    // Determine end condition
    const hasEndDate = cycle.terminationType === 'End on Date' && cycle.endDate;
    const hasRepeatLimit = cycle.terminationType === 'Limited Count' && cycle.repeatCount;
    const endDate = hasEndDate ? dayjs(cycle.endDate) : null;
    const maxCount = hasRepeatLimit ? Math.min(cycle.repeatCount!, maxPreviewCount) : maxPreviewCount;
    
    // currentDate = currentDate.add(cycleConfig.interval, 'month');
    if (cycle.monthDaySelection && cycle.monthDaySelection.length > 0) {
      // 调整到符合月份日期选择的第一个日期
      // 查找该月第一个匹配的日期
      let found = false;
      // 重置到该月的第一天
      // currentDate = currentDate.date(1);
      // 遍历查找该月的第一个匹配日期
      for (let i = currentDate.date(); i < 31; i++) {
        if (cycle.monthDaySelection.includes(currentDate.date())) {
          found = true;
          break;
        }
        // 如果已经是该月的最后一天，则跳出循环
        if (currentDate.date() >= 31 && currentDate.add(1, 'day').month() !== currentDate.month()) {
          break;
        }
        currentDate = currentDate.add(1, 'day');
      }
      
      // 如果该月没有匹配的日期，则找下个月的第一个匹配日期
      if (!found) {
        // 移动到下个月的第一天 已经加过了
        // currentDate = currentDate.add(1, 'month').date(1);
        // 查找下个月的第一个匹配日期
        const firstMatchingDay = cycle.monthDaySelection.find(day => day >= 1) || 1;
        currentDate = currentDate.date(firstMatchingDay);
      }
    }

    while (count < maxCount) {
      // Check if we've reached the end date
      if (endDate && currentDate.isAfter(endDate)) {
        break;
      }

      // Add current date to preview
      dates.push(currentDate.format('YYYY-MM-DD'));
      count++;

      if (cycle.monthDaySelection && cycle.monthDaySelection.length > 0) {
        // For monthly cycles with specific days
        let nextDate = currentDate.add(1, 'day');
        let found = false;
        
        // Look for the next occurrence within the current month
        while (nextDate.month() === currentDate.month()) {
          if (cycle.monthDaySelection.includes(nextDate.date())) {
            currentDate = nextDate;
            found = true;
            break;
          }
          nextDate = nextDate.add(1, 'day');
        }
        
        // If no more occurrences in current month, move to next interval
        if (!found) {
          currentDate = currentDate.add(cycle.interval, 'month');
          // Find first matching day in the new month
          const firstMatchingDay = Math.min(...cycle.monthDaySelection);
          currentDate = currentDate.date(firstMatchingDay);
        }
      } else {
        currentDate = currentDate.add(cycle.interval, 'month');
      }
    }
    return dates;
  }

  const calPreviewByYear = (cycle) => {
    if (!cycle.firstExecutionDate || !cycle.frequencyType) {
      return [];
    }

    const dates: string[] = [];
    const firstExecutionDate = dayjs(cycle.firstExecutionDate);
    let currentDate = firstExecutionDate.clone();
    // 如果 currentDate 在今天之前，则赋值为今天
    const today = dayjs().startOf('day');
    if (currentDate.isBefore(today)) {
      currentDate = today.clone();
    }
    const maxPreviewCount = 10; // Show up to 10 upcoming dates
    let count = 0;

    // Determine end condition
    const hasEndDate = cycle.terminationType === 'End on Date' && cycle.endDate;
    const hasRepeatLimit = cycle.terminationType === 'Limited Count' && cycle.repeatCount;
    const endDate = hasEndDate ? dayjs(cycle.endDate) : null;
    const maxCount = hasRepeatLimit ? Math.min(cycle.repeatCount!, maxPreviewCount) : maxPreviewCount;
    currentDate = currentDate.add(cycle.interval, 'year');

    while (count < maxCount) {
      // Check if we've reached the end date
      if (endDate && currentDate.isAfter(endDate)) {
        break;
      }

      // Add current date to preview
      dates.push(currentDate.format('YYYY-MM-DD'));
      count++;

      // Check if we've reached the end date
      currentDate = currentDate.add(cycle.interval, 'year');
    }
    return dates;
  }
  
  // Calculate next execution dates
  const calculateNextExecutions = (cycle: CycleConfig, count: number = 5) => {
    if ('Daily' === cycle.frequencyType) {
      return calPreviewByDay(cycle)
    } else if ('Weekly' === cycle.frequencyType) {
      return calPreviewByWeek(cycle)
    } else if ('Monthly' === cycle.frequencyType) {
      return calPreviewByMonth(cycle)
    } else {
      return calPreviewByYear(cycle)
    }
    // const executions: string[] = [];
    // const firstExecutionDate = dayjs(cycle.firstExecutionDate);
    // let currentDate = firstExecutionDate.clone();
    // let execCount = 0;

    // // Determine end condition
    // const hasEndDate = cycle.terminationType === 'End on Date' && cycle.endDate;
    // const hasRepeatLimit = cycle.terminationType === 'Limited Count' && cycle.repeatCount;
    // const endDate = hasEndDate ? dayjs(cycle.endDate) : null;
    // const maxCount = hasRepeatLimit ? Math.min(cycle.repeatCount!, count) : count;

    // // 根据频率类型计算第一个符合规律的执行日期（在首次执行日期之后）
    // switch (cycle.frequencyType) {
    //   case 'Daily':
    //     // 对于每日执行，第一个执行日期就是首次执行日期加上间隔天数
    //     currentDate = currentDate.add(cycle.interval, 'day');
    //     break;
        
    //   case 'Weekly':
    //     // 对于每周执行，需要找到首次执行日期之后符合星期选择的第一个日期
    //     if (cycle.weekDaySelection && cycle.weekDaySelection.length > 0) {
    //       // 先将日期加一天，确保我们查找的是首次执行日期之后的日期
    //       currentDate = currentDate.add(1, 'day');
          
    //       // 寻找第一个匹配的星期几
    //       while (!cycle.weekDaySelection.includes(currentDate.day())) {
    //         currentDate = currentDate.add(1, 'day');
    //       }
    //     } else {
    //       // 如果没有选择特定的星期几，默认按照间隔周计算
    //       currentDate = currentDate.add(cycle.interval, 'week');
    //     }
    //     break;
        
    //   case 'Monthly':
    //     // 对于每月执行，需要找到首次执行日期之后符合月份日期选择的第一个日期
    //     currentDate = currentDate.add(cycle.interval, 'month');
    //     if (cycle.monthDaySelection && cycle.monthDaySelection.length > 0) {
    //       // 调整到符合月份日期选择的第一个日期
    //       // 查找该月第一个匹配的日期
    //       let found = false;
    //       // 重置到该月的第一天
    //       currentDate = currentDate.date(1);
    //       // 遍历查找该月的第一个匹配日期
    //       for (let i = 0; i < 31; i++) {
    //         if (cycle.monthDaySelection.includes(currentDate.date())) {
    //           found = true;
    //           break;
    //         }
    //         // 如果已经是该月的最后一天，则跳出循环
    //         if (currentDate.date() >= 28 && currentDate.add(1, 'day').month() !== currentDate.month()) {
    //           break;
    //         }
    //         currentDate = currentDate.add(1, 'day');
    //       }
          
    //       // 如果该月没有匹配的日期，则找下个月的第一个匹配日期
    //       if (!found) {
    //         // 移动到下个月的第一天
    //         currentDate = currentDate.add(1, 'month').date(1);
    //         // 查找下个月的第一个匹配日期
    //         const firstMatchingDay = cycle.monthDaySelection.find(day => day >= 1) || 1;
    //         currentDate = currentDate.date(firstMatchingDay);
    //       }
    //     }
    //     break;
        
    //   case 'Yearly':
    //     // 对于年度执行，第一个执行日期就是首次执行日期加上间隔年数
    //     currentDate = currentDate.add(cycle.interval, 'year');
    //     break;
        
    //   default:
    //     break;
    // }

    // // 根据频率类型计算后续的执行日期
    // while (execCount < maxCount) {
    //   // Check if we've reached the end date
    //   if (endDate && currentDate.isAfter(endDate)) {
    //     break;
    //   }

    //   // Add current date to preview
    //   executions.push(currentDate.format('YYYY/MM/DD'));
    //   execCount++;

    //   // Calculate next date based on frequency type
    //   switch (cycle.frequencyType) {
    //     case 'Daily':
    //       currentDate = currentDate.add(cycle.interval, 'day');
    //       break;

    //     case 'Weekly':
    //       if (cycle.weekDaySelection && cycle.weekDaySelection.length > 0) {
    //         // For weekly cycles with specific weekdays
    //         let nextDate = currentDate.add(1, 'day');
    //         let found = false;
            
    //         // Look for the next occurrence within the current week
    //         for (let i = 0; i < 7; i++) {
    //           if (cycle.weekDaySelection.includes(nextDate.day())) {
    //             currentDate = nextDate;
    //             found = true;
    //             break;
    //           }
    //           nextDate = nextDate.add(1, 'day');
    //         }
            
    //         // If no more occurrences in current week, move to next interval
    //         if (!found) {
    //           // Move to the start of next interval
    //           currentDate = currentDate.add(cycle.interval - 1, 'week');
    //           // Find first matching weekday in the new week
    //           for (let i = 0; i < 7; i++) {
    //             if (cycle.weekDaySelection.includes(currentDate.day())) {
    //               break;
    //             }
    //             currentDate = currentDate.add(1, 'day');
    //           }
    //         }
    //       } else {
    //         currentDate = currentDate.add(cycle.interval, 'week');
    //       }
    //       break;

    //     case 'Monthly':
    //       if (cycle.monthDaySelection && cycle.monthDaySelection.length > 0) {
    //         // For monthly cycles with specific days
    //         let nextDate = currentDate.add(1, 'day');
    //         let found = false;
            
    //         // Look for the next occurrence within the current month
    //         while (nextDate.month() === currentDate.month()) {
    //           if (cycle.monthDaySelection.includes(nextDate.date())) {
    //             currentDate = nextDate;
    //             found = true;
    //             break;
    //           }
    //           nextDate = nextDate.add(1, 'day');
    //         }
            
    //         // If no more occurrences in current month, move to next interval
    //         if (!found) {
    //           currentDate = currentDate.add(cycle.interval, 'month');
    //           // Find first matching day in the new month
    //           const firstMatchingDay = Math.min(...cycle.monthDaySelection);
    //           currentDate = currentDate.date(firstMatchingDay);
    //         }
    //       } else {
    //         currentDate = currentDate.add(cycle.interval, 'month');
    //       }
    //       break;

    //     case 'Yearly':
    //       currentDate = currentDate.add(cycle.interval, 'year');
    //       break;

    //     default:
    //       break;
    //   }
      
    //   // Check termination conditions
    //   if (cycle.terminationType === 'End on Date' && cycle.endDate) {
    //     if (currentDate.isAfter(endDate)) break;
    //   }
    //   if (cycle.terminationType === 'Limited Count' && cycle.repeatCount) {
    //     if (execCount >= cycle.repeatCount) break;
    //   }
    // }
    
    // return executions;
  };

  const nextExecutions = calculateNextExecutions(cycle);

  return (
    <Card 
      title={
        <Space>
          <ScheduleOutlined />
          {t('planTemplate.detail.cycleConfig.title')}
        </Space>
      }
    >
      <Row gutter={[24, 16]}>
        <Col span={12}>
          <Card size="small" title={t('planTemplate.detail.cycleConfig.cycleSettings')} style={{ height: '100%' }}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label={t('planTemplate.detail.cycleConfig.frequencyType')}>
                <Tag color="blue" icon={<CalendarOutlined />}>{cycle.frequencyType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.cycleConfig.executionFrequency')}>
                <Text strong>{getFrequencyDescription()}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.cycleConfig.firstExecutionTime')}>
                <Space>
                  <CalendarOutlined style={{ color: '#1890ff' }} />
                  <Text strong>{dayjs(cycle.firstExecutionDate).format('YYYY/MM/DD')}</Text>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.cycleConfig.terminationCondition')}>
                <Text>{getTerminationDescription()}</Text>
              </Descriptions.Item>
              <Descriptions.Item label={t('planTemplate.detail.cycleConfig.cycleStatus')}>
                <Badge 
                  status={cycle.cycleStatus === 'Active' ? 'processing' : 'default'} 
                  text={getStatusTranslation(cycle.cycleStatus)}
                />
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
        <Col span={12}>
          <Card size="small" title={t('planTemplate.detail.cycleConfig.executionPreview')} style={{ height: '100%' }}>
            {nextExecutions.length > 0 ? (
              <List
                size="small"
                dataSource={nextExecutions}
                renderItem={(date, index) => (
                  <List.Item>
                    <Space>
                      <Avatar size="small" style={{ backgroundColor: '#1890ff' }}>
                        {index + 1}
                      </Avatar>
                      <Text>{date}</Text>
                      {index === 0 && <Tag color="green">{t('planTemplate.detail.cycleConfig.nextExecutionLabel')}</Tag>}
                    </Space>
                  </List.Item>
                )}
              />
            ) : (
              <Empty 
                image={Empty.PRESENTED_IMAGE_SIMPLE} 
                description={t('planTemplate.detail.cycleConfig.noExecutableCycles')}
              />
            )}
          </Card>
        </Col>
      </Row>

      {(cycle.weekDaySelection && cycle.weekDaySelection.length > 0) && (
        <>
          <Divider />
          <div>
            <Text strong>{t('planTemplate.detail.cycleConfig.selectedWeekdays')}：</Text>
            <Space wrap style={{ marginTop: 8 }}>
              {cycle.weekDaySelection.map(day => (
                <Tag key={day} color="blue">
                  {getWeekdayTranslation(day)}
                </Tag>
              ))}
            </Space>
          </div>
        </>
      )}

      {(cycle.monthDaySelection && cycle.monthDaySelection.length > 0) && (
        <>
          <Divider />
          <div>
            <Text strong>{t('planTemplate.detail.cycleConfig.selectedDates')}：</Text>
            <Space wrap style={{ marginTop: 8 }}>
              {cycle.monthDaySelection.map(day => (
                <Tag key={day} color="green">{day}{t('planTemplate.detail.cycleConfig.dayUnit')}</Tag>
              ))}
            </Space>
          </div>
        </>
      )}

      {(cycle.lastGenerationTime || cycle.nextGenerationTime) && (
        <>
          <Divider />
          <Row gutter={24}>
            {cycle.lastGenerationTime && (
              <Col span={12}>
                <Statistic
                  title={t('planTemplate.detail.cycleConfig.lastGenerationTime')}
                  value={dayjs(cycle.lastGenerationTime).format('YYYY/MM/DD')}
                  prefix={<CalendarOutlined />}
                />
              </Col>
            )}
            {cycle.nextGenerationTime && (
              <Col span={12}>
                <Statistic
                  title={t('planTemplate.detail.cycleConfig.nextGenerationTime')}
                  value={dayjs(cycle.nextGenerationTime).format('YYYY/MM/DD')}
                  prefix={<CalendarOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Col>
            )}
          </Row>
        </>
      )}
    </Card>
  );
};

export default CycleConfiguration;